import { FC, FocusEventHandler, Fragment, MouseEventHandler, ReactNode, useRef, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { IconCheckThin, IconChevronDown, IconSearch } from 'components/Icons';
import { Tooltip } from 'components/PlatformResourceSelector/Tooltip';
import { css, cx } from '@linaria/core';
import { useDebounce } from 'react-use';
import { breakpointConfig } from 'styles/utils';
interface SelectorOption {
  name: string;
  value: string;
  icon?: ReactNode;
  tooltip?: string;
}
export interface SelectorProps {
  options: {
    title: string;
    optionsList: SelectorOption[];
  }[];
  defaultValue?: string;
  showSearchIcon?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}
function findOptionByValue(options: SelectorProps['options'], value: string): SelectorOption | undefined {
  for (const group of options) {
    const found = group.optionsList.find(option => option.value === value);
    if (found) return found;
  }
  return undefined;
}
export const Selector: FC<SelectorProps> = ({
  options,
  defaultValue,
  value,
  onChange,
  showSearchIcon = false
}) => {
  const comboboxInputRef = useRef<HTMLInputElement>(null);
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const comboboxOptionsRef = useRef<HTMLUListElement>(null);
  const selectedOption = findOptionByValue(options, value ?? defaultValue ?? '');
  const [query, setQuery] = useState('');
  const [searchIcon, setSearchIcon] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  useDebounce(() => {
    if (!query.length) return setFilteredOptions(options);
    return setFilteredOptions(options.map(optionSection => {
      return {
        title: optionSection.title,
        optionsList: optionSection.optionsList.filter(model => model.name.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')))
      };
    }));
  }, 250, [query, options]);
  const focusHandler: FocusEventHandler<HTMLInputElement> = event => {
    // Clear the search box, and focus it:
    event.target.value = '';
    event.target.select();

    // combobox will not open, open it manually...
    if (!comboboxOptionsRef.current) {
      // Show search icon and open the combobox
      setSearchIcon(true);
      comboboxButtonRef.current?.click();
    } else {
      // Unselect the text after choosing an option
      setSearchIcon(false);
      // Reset the search query
      setQuery('');
      // We chose something, so blur away from the input
      event.target.blur();
    }
  };
  const blurHandler: FocusEventHandler<HTMLInputElement> = () => {
    // Reset the search icon
    setSearchIcon(false);
  };
  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = event => {
    // Focus the input on button click
    comboboxInputRef.current?.focus();

    // If it's already open, make sure we don't bubble and keep the search state as is...
    if (comboboxOptionsRef.current) {
      event?.preventDefault();
      setSearchIcon(true);
    }
  };
  const handleChange = (option: SelectorOption) => {
    // Choose the option
    onChange?.(option.value);
    // Reset the search icon
    setSearchIcon(false);
    // reset the search query:
    setQuery('');
    // Defocus the input
    comboboxInputRef.current?.blur();
  };
  const renderSearchIcon = showSearchIcon && searchIcon;
  return <Combobox value={selectedOption} onChange={handleChange}>
      <div className={ComboboxContainerStyle}>
        <div className={ComboboxInputContainerStyle}>
          {renderSearchIcon ? <div className={InputModelIconStyle}>
              <IconSearch color="#667085" />
            </div> : selectedOption?.icon ? <div className={InputModelIconStyle}>{selectedOption.icon}</div> : null}
          <Combobox.Input ref={comboboxInputRef} placeholder="Search..." displayValue={(selectedOptionObject?: SelectorOption) => selectedOptionObject?.name ?? ''} onBlur={blurHandler} onFocus={focusHandler} className={cx(ComboboxInputStyle, (selectedOption?.icon || renderSearchIcon) && ComboboxInputStyleIconPadding)} onChange={event => setQuery(event.target.value)} />
          <Combobox.Button ref={comboboxButtonRef} className={ComboboxChevronButtonStyle} onClick={handleButtonClick}>
            <IconChevronDown className={ComboboxChevronIconStyle} />
          </Combobox.Button>
        </div>
        <Combobox.Options ref={comboboxOptionsRef} id="options-container" className={ComboboxOptionsStyle}>
          {filteredOptions.map(optionSection => {
          if (!optionSection.optionsList.length) return null;
          return <Fragment key={`${optionSection.title}-${optionSection.optionsList.length}`}>
                <div className={ModelSectionStyle}>{optionSection.title}</div>
                <div className={ModelOptionsContainerStyle}>
                  {optionSection.optionsList.map(option => {
                return <Combobox.Option key={option.name} className={({
                  active,
                  selected
                }) => `${ModelOptionStyle} ${active || selected ? ModelOptionStyleActive : ''}`} value={option}>
                        {({
                    selected
                  }) => {
                    return <>
                              {option.icon ? <div className={ModelIconStyle}>{option.icon}</div> : null}
                              <Tooltip initialState={{
                        placement: 'right'
                      }} text={option.tooltip || option.name}>
                                <span className={ModelOptionText}>{option.name}</span>
                              </Tooltip>
                              {selected ? <IconCheckThin /> : null}
                            </>;
                  }}
                      </Combobox.Option>;
              })}
                </div>
              </Fragment>;
        })}
        </Combobox.Options>
      </div>
    </Combobox>;
};
export const ComboboxContainerStyle = "odules__Playground__Components__Selector_tsx-ComboboxContainerStyle-c1qh9zpj";
const ComboboxInputContainerStyle = "odules__Playground__Components__Selector_tsx-ComboboxInputContainerStyle-c1mdnwx8";
const InputModelIconStyle = "odules__Playground__Components__Selector_tsx-InputModelIconStyle-izo22mn";
const ComboboxInputStyle = "odules__Playground__Components__Selector_tsx-ComboboxInputStyle-c1ae3xfa";
const ComboboxInputStyleIconPadding = "odules__Playground__Components__Selector_tsx-ComboboxInputStyleIconPadding-c1lj7vyk";
const ComboboxChevronButtonStyle = "odules__Playground__Components__Selector_tsx-ComboboxChevronButtonStyle-c1tq23m5";
const ComboboxChevronIconStyle = "odules__Playground__Components__Selector_tsx-ComboboxChevronIconStyle-cswclae";
const ComboboxOptionsStyle = "odules__Playground__Components__Selector_tsx-ComboboxOptionsStyle-c1mns92p";
const ModelSectionStyle = "odules__Playground__Components__Selector_tsx-ModelSectionStyle-m1dc591o";
const ModelOptionsContainerStyle = "odules__Playground__Components__Selector_tsx-ModelOptionsContainerStyle-mb8x7zf";
const ModelOptionStyle = "odules__Playground__Components__Selector_tsx-ModelOptionStyle-mpd052i";
const ModelOptionStyleActive = "odules__Playground__Components__Selector_tsx-ModelOptionStyleActive-mi0xbem";
const ModelIconStyle = "odules__Playground__Components__Selector_tsx-ModelIconStyle-mxx1h7v";
const ModelOptionText = "odules__Playground__Components__Selector_tsx-ModelOptionText-m19da87l";

require("./Selector.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./Selector.tsx");